import { VALUE_CHAIN_MAP_CUSTOM_NODES, VALUE_CHAIN_UNIT_TYPES } from 'constants/interfaces';
import { getUnitDisplayName } from 'utils/reactFlowHelpers';

import type { Node } from '@xyflow/react';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';

export interface IMonitoringEntityRef {
  unitId: number,
  name: string,
}

const BOARD_RESOLVABLE_UNIT_TYPES = new Set<string>([
  VALUE_CHAIN_UNIT_TYPES.SUPPLIER,
  VALUE_CHAIN_UNIT_TYPES.PRODUCT,
  VALUE_CHAIN_UNIT_TYPES.OUTPUT_PRODUCT,
]);

const collectBoardEntityIndexes = (nodes: Node[]): {
  nodesById: Map<number, IMonitoringEntityRef>,
  nodesByName: Map<string, IMonitoringEntityRef>,
} => {
  const nodesById = new Map<number, IMonitoringEntityRef>();
  const nodesByName = new Map<string, IMonitoringEntityRef>();

  nodes.forEach((node) => {
    if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
      return;
    }

    const nodeData = node.data as unknown as ICustomUnitNodeData;

    if (!BOARD_RESOLVABLE_UNIT_TYPES.has(String(nodeData.type || ''))
      || !Number.isInteger(nodeData.id)) {
      return;
    }

    const name = getUnitDisplayName(nodeData).trim();

    if (!name) {
      return;
    }

    const ref: IMonitoringEntityRef = {
      unitId: nodeData.id,
      name,
    };

    nodesById.set(nodeData.id, ref);
    nodesByName.set(name.toLowerCase(), ref);
  });

  return { nodesById, nodesByName };
};

export const getBoardResolvableUnitIds = (nodes: Node[]): Set<number> => (
  new Set(collectBoardEntityIndexes(nodes).nodesById.keys())
);

export const resolveMonitoringEntitiesFromBoard = (
  nodes: Node[],
  entities: IMonitoringEntityRef[],
): IMonitoringEntityRef[] => {
  if (!entities.length || !nodes.length) {
    return entities.filter((entity) => entity.unitId > 0);
  }

  const { nodesById, nodesByName } = collectBoardEntityIndexes(nodes);

  const resolvedEntities = entities
    .map((entity) => {
      if (entity.unitId > 0 && nodesById.has(entity.unitId)) {
        return nodesById.get(entity.unitId)!;
      }

      const matchedEntity = nodesByName.get(entity.name.trim().toLowerCase());

      if (matchedEntity) {
        return matchedEntity;
      }

      return entity.unitId > 0 ? entity : null;
    })
    .filter((entity): entity is IMonitoringEntityRef => entity !== null);

  return resolvedEntities.length ? resolvedEntities : entities.filter((entity) => entity.unitId > 0);
};
